import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, take } from 'rxjs';

import { Content, OivCoreSharedFacade, OivError, OivErrorHandler } from '@mobi/oiv-viewer-utils-ng-jslib';
import { OIV_TYPE } from '@mobi/oiv-viewer-xml-parser-ng-jslib';
import { LoadingState } from '@mobi/rwc-uxframework-ng';

import { Helper } from '../../../../share/helper';
import { EidNotFoundError } from '../../error-handling/eid-not-found.error';
import { OivViewerFacade } from '../facade/oiv-viewer-facade';

export const EID_QUERY_PARAM = 'eID';
export const EID_NOT_FOUND_TRANSLATION_KEY = 'oiv.error.eId.notFound';

@Injectable({ providedIn: 'root' })
export class EidNavigationService {
    #route = inject(ActivatedRoute);
    #router = inject(Router);
    #helper = inject(Helper);
    #oivViewerFacade = inject(OivViewerFacade);
    #coreSharedFacade = inject(OivCoreSharedFacade);
    #oivErrorHandler = inject(OivErrorHandler);

    /**
     * Reads the `eID` query param from the current route. If present, waits for the
     * AVB content to be fully loaded and then resolves the eID to the correct chapter/
     * topic URL before navigating there (replacing the current history entry so the
     * transient `eID` param does not appear in the browser back stack).
     *
     * Unknown eIDs redirect to the homepage without logging an error. Valid XML eIDs
     * without a rendered target simply open the document normally.
     */
    handleEidQueryParam(destroyRef: DestroyRef): void {
        const eId = this.#route.snapshot.queryParamMap.get(EID_QUERY_PARAM);
        if (!eId) {
            return;
        }

        this.#oivViewerFacade
            .getLoadingIndex()
            .pipe(
                filter(state => state === LoadingState.READY),
                take(1),
                takeUntilDestroyed(destroyRef),
            )
            .subscribe(() => {
                try {
                    const urlPath = this.#resolveEidToUrl(eId);
                    this.#router.navigateByUrl(urlPath, { replaceUrl: true });
                } catch (error) {
                    if (!(error instanceof EidNotFoundError)) {
                        console.error('Failed to navigate to eID.', {
                            eId,
                            currentUrl: this.#router.url,
                            error,
                        });
                        this.#oivErrorHandler.handleError(new OivError('Failed to navigate to eID.'));
                    }
                    this.#router.navigateByUrl(this.#buildHomePageUrl(), { replaceUrl: true });
                }
            });
    }

    #resolveEidToUrl(eId: string): string {
        const chapters = this.#oivViewerFacade.avbChapters();
        const rootChapter: Content = {
            eId: 'avb',
            type: OIV_TYPE.CHAPTER_TITLE,
            content: chapters,
        };

        const requestedContent = this.#helper.searchContentByEid(eId, rootChapter);
        const isChapterEid = requestedContent?.type === OIV_TYPE.CHAPTER || !eId.includes('/');

        let target: Content | null;
        if (isChapterEid) {
            target = this.#findRenderableDescendant(rootChapter, eId);
        } else {
            target = requestedContent ?? this.#findClosestRenderableAncestor(rootChapter, eId);
        }
        if (!target) {
            throw new EidNotFoundError(eId);
        }

        const pathOnlyRouter = { url: this.#helper.getPathUrl(this.#router) } as Router;
        const baseUrl = this.#helper.getBaseUrl(
            pathOnlyRouter,
            this.#coreSharedFacade.appContext(),
            this.#coreSharedFacade.isApprovalMode(),
        );

        const queryUrl = this.#getQueryUrlWithoutEid();
        return this.#helper.getUrlPath(target, rootChapter, baseUrl, queryUrl);
    }

    /**
     * Finds the closest rendered ancestor of the requested element.
     * Some structural XML elements are flattened by the parser, so the requested
     * eID may not be a navigation target even though its ancestor is.
     */
    #findClosestRenderableAncestor(rootChapter: Content, eId: string): Content | null {
        const segments = eId.split('/');

        while (segments.pop()) {
            const content = this.#helper.searchContentByEid(segments.join('/'), rootChapter);
            if (content) {
                return content;
            }
        }
        return null;
    }

    /**
     * Chapters need an inner target so navigation opens their first content page.
     * This lookup also proves that a flattened structural eID exists in the tree.
     */
    #findRenderableDescendant(content: Content, eId: string): Content | null {
        if (content.eId.startsWith(`${eId}/`) && content.type !== OIV_TYPE.CHAPTER) {
            return content;
        }
        if (!Array.isArray(content.content)) {
            return null;
        }

        for (const child of content.content) {
            const target = typeof child === 'string' ? null : this.#findRenderableDescendant(child, eId);
            if (target) {
                return target;
            }
        }
        return null;
    }

    /**
     * Builds the homepage URL using the same logic as chapter-page components.
     * Correctly handles language prefix, approval mode and B2C vs B2E contexts.
     */
    #buildHomePageUrl(): string {
        const pathOnlyRouter = { url: this.#helper.getPathUrl(this.#router) } as Router;
        return this.#helper.getHomePageURL(
            pathOnlyRouter,
            this.#coreSharedFacade.appContext(),
            this.#coreSharedFacade.isApprovalMode(),
        );
    }

    /** Returns the current query string with the `eID` param stripped out. */
    #getQueryUrlWithoutEid(): string {
        const [baseAndQuery] = this.#router.url.split('#');
        const queryString = baseAndQuery.split('?')[1];
        if (!queryString) {
            return '';
        }
        const filtered = queryString.split('&').filter(param => !param.startsWith(`${EID_QUERY_PARAM}=`));
        return filtered.join('&');
    }
}
